import { useEffect, useMemo, useState } from "react";
import { RotateCcw, RefreshCw, Ban, Check, X, ShieldCheck, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { fmtDate, fmtDateTime, formatIDR, DIVISION_COLOR } from "@/lib/constants";
import { COMMON_STATUS_LABEL } from "@/lib/i18nLabels";

const STATUS_META = {
  pending:  { label: COMMON_STATUS_LABEL.pending,  cls: "bg-amber-100 text-amber-800 border-amber-300" },
  verified: { label: COMMON_STATUS_LABEL.verified, cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  rejected: { label: COMMON_STATUS_LABEL.rejected, cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

/**
 * PettyCashReturnsHistory — pengembalian kas dengan alur verifikasi FAT/BOD.
 * Staff view: milik sendiri; reviewer (FAT/BOD): semua + tombol Verify/Reject.
 * (08-Feb-2026): "Pengembalian Kas harus diverifikasi oleh aprover".
 */
export default function PettyCashReturnsHistory({ isReviewer, currentUserId, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [exportMonth, setExportMonth] = useState(() => new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [exportStatus, setExportStatus] = useState("all");
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportMonth) params.set("month", exportMonth);
      if (exportStatus && exportStatus !== "all") params.set("status", exportStatus);
      const url = `/petty-cash/returns/export.xlsx?${params.toString()}`;
      const { data } = await api.get(url, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const monthSuffix = exportMonth || new Date().toISOString().slice(0, 10);
      const statusSuffix = exportStatus !== "all" ? `_${exportStatus}` : "";
      link.download = `riwayat_pengembalian_kas_${monthSuffix}${statusSuffix}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`Excel diunduh — ${monthSuffix}${statusSuffix}`);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal export");
    } finally {
      setExporting(false);
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/petty-cash/returns")
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const cancelReturn = async (r) => {
    if (!window.confirm(`Batalkan pengembalian Rp${(r.amount || 0).toLocaleString("id-ID")} tanggal ${fmtDate(r.return_date)}?`)) return;
    try { await api.delete(`/petty-cash/returns/${r.id}`); toast.success("Pengembalian dibatalkan"); load(); onChanged?.(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const verifyReturn = async (r) => {
    try {
      await api.post(`/petty-cash/returns/${r.id}/verify`);
      toast.success(`Pengembalian ${r.user_name} terverifikasi`);
      load(); onChanged?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const submitReject = async (r) => {
    if (!rejectNote.trim()) { toast.error("Alasan reject wajib diisi"); return; }
    try {
      await api.post(`/petty-cash/returns/${r.id}/reject`, { note: rejectNote.trim() });
      toast.success("Pengembalian ditolak");
      setRejectingId(null); setRejectNote("");
      load(); onChanged?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const filtered = q.trim()
    ? rows.filter((r) => {
        const s = q.trim().toLowerCase();
        return (r.user_name || "").toLowerCase().includes(s)
          || (r.note || "").toLowerCase().includes(s)
          || (r.company_bank_account?.bank_name || "").toLowerCase().includes(s)
          || (r.status || "").toLowerCase().includes(s);
      })
    : rows;
  const totals = filtered.reduce((acc, r) => { const st = r.status || "pending"; acc[st] = (acc[st] || 0) + (r.amount || 0); return acc; }, {});
  const colCount = isReviewer ? 8 : 7;

  return (
    <div className="bg-white border border-slate-200" data-testid="pc-returns-history">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-wrap gap-2">
        <div>
          <h3 className="font-display text-base tracking-tight text-[#0B1D3A] inline-flex items-center gap-2">
            <RotateCcw size={15} className="text-emerald-700" /> Riwayat Pengembalian Kas
          </h3>
          <p className="text-[10px] text-slate-500">
            {isReviewer ? "Semua staff · " : "Milik Anda · "}{filtered.length} pengembalian
            {" · "}<span className="text-amber-700 font-semibold">Pending {formatIDR(totals.pending || 0)}</span>
            {" · "}<span className="text-emerald-700 font-semibold">Verified {formatIDR(totals.verified || 0)}</span>
            {(totals.rejected || 0) > 0 && <> · <span className="text-rose-700 font-semibold">Rejected {formatIDR(totals.rejected)}</span></>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="input-returns-search" type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari staff / catatan / bank / status…" className="px-2 py-1.5 text-xs border border-slate-300 rounded-none focus:outline-none focus:border-[#FF5A1F] w-64" />
          <button data-testid="btn-returns-refresh" onClick={load} className="p-1.5 text-slate-500 hover:text-[#0B1D3A] border border-slate-200 hover:border-slate-400"><RefreshCw size={13} /></button>
        </div>
      </div>
      {/* Export bar — filter by bulan + status → download Excel */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50 flex-wrap text-[10px]">
        <FileDown size={12} className="text-slate-500" />
        <span className="uppercase tracking-wider font-semibold text-slate-600">Export Excel</span>
        <label className="text-slate-500">Bulan:</label>
        <input
          data-testid="input-return-export-month"
          type="month"
          value={exportMonth}
          onChange={(e) => setExportMonth(e.target.value)}
          className="border border-slate-300 px-1.5 py-1 text-[10px] tabular-nums bg-white"
        />
        <label className="text-slate-500 ml-2">Status:</label>
        <select
          data-testid="select-return-export-status"
          value={exportStatus}
          onChange={(e) => setExportStatus(e.target.value)}
          className="border border-slate-300 px-1.5 py-1 text-[10px] bg-white"
        >
          <option value="all">Semua</option>
          <option value="pending">Pending</option>
          <option value="verified">Verified</option>
          <option value="rejected">Rejected</option>
        </select>
        <button
          data-testid="btn-export-returns"
          onClick={doExport}
          disabled={exporting}
          className="ml-auto px-3 py-1 text-[10px] uppercase tracking-wider font-semibold text-white bg-[#0B1D3A] hover:bg-[#132a5b] disabled:opacity-50 inline-flex items-center gap-1"
        >
          <FileDown size={11} /> {exporting ? "Menyiapkan…" : "Download Excel"}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 uppercase tracking-wider text-[10px] text-left">
            <tr>
              <th className="px-3 py-2">Tgl Kembali</th>
              {isReviewer && <th className="px-3 py-2">Staff</th>}
              <th className="px-3 py-2 text-right">Nominal</th>
              <th className="px-3 py-2">Rekening Tujuan</th>
              <th className="px-3 py-2">Catatan</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Diproses</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody data-testid="returns-body">
            {loading && <tr><td colSpan={colCount} className="px-3 py-6 text-center text-slate-400 italic">Memuat…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={colCount} className="px-3 py-6 text-center text-slate-400 italic">Belum ada pengembalian kas.</td></tr>}
            {!loading && filtered.map((r) => {
              const status = r.status || "pending";
              const meta = STATUS_META[status];
              const canCancel = (isReviewer || (r.user_id === currentUserId && (() => {
                try { return (Date.now() - new Date(r.created_at).getTime()) <= 3600000; } catch { return false; }
              })())) && status === "pending";
              const canVerify = isReviewer && status === "pending" && r.user_id !== currentUserId;
              const divCls = DIVISION_COLOR[r.division] || "bg-slate-100 text-slate-700 border-slate-300";
              const isRejecting = rejectingId === r.id;
              return (
                <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 ${status === "rejected" ? "opacity-70" : ""}`} data-testid={`return-row-${r.id}`}>
                  <td className="px-3 py-2 tabular-nums">{fmtDate(r.return_date)}</td>
                  {isReviewer && (
                    <td className="px-3 py-2">
                      <div className="font-medium text-[#0B1D3A]">{r.user_name}</div>
                      {r.division && <span className={`inline-flex mt-0.5 px-1 py-0.5 text-[9px] font-semibold uppercase border ${divCls}`}>{r.division}</span>}
                    </td>
                  )}
                  <td className={`px-3 py-2 tabular-nums text-right font-semibold ${status === "verified" ? "text-emerald-700" : status === "rejected" ? "text-slate-500 line-through" : "text-amber-700"}`}>{formatIDR(r.amount)}</td>
                  <td className="px-3 py-2 text-[11px]">
                    {r.company_bank_account ? (<>
                      <div className="font-medium">{r.company_bank_account.bank_name}</div>
                      <div className="font-mono text-[10px] text-slate-500">{r.company_bank_account.account_no}</div>
                    </>) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-600 italic max-w-xs truncate" title={r.note || ""}>{r.note || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${meta.cls}`} data-testid={`return-status-${r.id}`}>
                      {status === "verified" && <ShieldCheck size={9} />}{meta.label}
                    </span>
                    {status === "rejected" && r.reject_note && (<div className="text-[9px] text-rose-700 italic mt-0.5 max-w-[180px] truncate" title={r.reject_note}>"{r.reject_note}"</div>)}
                  </td>
                  <td className="px-3 py-2 text-[10px] text-slate-500">
                    {status === "verified" && r.verified_at && (<>
                      <div className="text-emerald-700">✓ {r.verified_by_name}</div>
                      <div className="tabular-nums">{fmtDateTime(r.verified_at)}</div>
                    </>)}
                    {status === "rejected" && r.rejected_at && (<>
                      <div className="text-rose-700">✗ {r.rejected_by_name}</div>
                      <div className="tabular-nums">{fmtDateTime(r.rejected_at)}</div>
                    </>)}
                    {status === "pending" && <span className="text-slate-400 italic">menunggu verifikasi</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isRejecting ? (
                      <div className="flex flex-col gap-1 items-end">
                        <input autoFocus placeholder="Alasan tolak *" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} className="border border-slate-300 px-1.5 py-0.5 text-[10px] w-40" data-testid={`input-reject-return-${r.id}`} />
                        <div className="flex gap-1">
                          <button onClick={() => submitReject(r)} data-testid={`btn-confirm-reject-return-${r.id}`} className="text-[10px] font-semibold uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 px-2 py-0.5">Kirim</button>
                          <button onClick={() => { setRejectingId(null); setRejectNote(""); }} className="text-[10px] text-slate-600 border border-slate-300 hover:bg-slate-100 px-2 py-0.5">✕</button>
                        </div>
                      </div>
                    ) : (
                      <div className="inline-flex gap-1 justify-end flex-wrap">
                        {canVerify && (<button onClick={() => verifyReturn(r)} data-testid={`btn-verify-return-${r.id}`} className="text-[10px] uppercase tracking-wider font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-2 py-0.5 inline-flex items-center gap-1"><Check size={9} /> Verify</button>)}
                        {canVerify && (<button onClick={() => { setRejectingId(r.id); setRejectNote(""); }} data-testid={`btn-reject-return-${r.id}`} className="text-[10px] uppercase tracking-wider font-semibold text-white bg-rose-600 hover:bg-rose-700 px-2 py-0.5 inline-flex items-center gap-1"><X size={9} /> Reject</button>)}
                        {canCancel && (<button onClick={() => cancelReturn(r)} data-testid={`btn-cancel-return-${r.id}`} className="text-[10px] uppercase tracking-wider text-slate-600 hover:bg-slate-100 border border-slate-300 px-2 py-0.5 inline-flex items-center gap-1"><Ban size={9} /> Batal</button>)}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
